import {
  ApiChartRank,
  ApiChartRankDrection,
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
} from "@incendium/api";
import { IChart } from "Interfaces";
import { useCallback, useState } from "react";
import { getPrimaryAnalytics } from "features/analytics";

export type TopProductsStrategyKey =
  | "top_sellers"
  | "best_converting_products"
  | "highly_clicked_products"
  | "cart_magnets"
  | "hidden_gems"
  | "popular_with_returning_customers"
  | "new_customer_favourites";

export const CUSTOM_TOP_PRODUCTS_METRIC_OPTIONS: Array<{
  value: ApiMetric;
  label: string;
}> = [
  { value: ApiMetric.METRIC_PRODUCT_REVENUE, label: "Revenue" },
  { value: ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT, label: "Product page views" },
  { value: ApiMetric.METRIC_VISIT_TO_PURCHASE_RATE, label: "Conversion rate" },
  { value: ApiMetric.METRIC_PRODUCT_CLICK_THROUGH_RATE, label: "Product CTR" },
  { value: ApiMetric.METRIC_PAGE_ADD_TO_CART_CLICK_RATE, label: "Add-to-cart rate" },
  { value: ApiMetric.METRIC_PAGE_ADD_TO_CART_CLICK_COUNT, label: "Add-to-cart count" },
  {
    value: ApiMetric.METRIC_RETURNING_CUSTOMER_RATE,
    label: "Returning customer purchase rate",
  },
  {
    value: ApiMetric.METRIC_RETURNING_CUSTOMERS_COUNT,
    label: "Returning customer purchase count",
  },
  {
    value: ApiMetric.METRIC_TO_NEW_CUSTOMER_RATE,
    label: "New customer purchase rate",
  },
  { value: ApiMetric.METRIC_NEW_CUSTOMERS_COUNT, label: "New customer purchase count" },
  { value: ApiMetric.METRIC_SALES_COUNT, label: "Sales count" },
  { value: ApiMetric.METRIC_SALES_REVENUE, label: "Sales revenue" },
];

export interface TopProductsStrategy {
  label: string;
  description: string;
  ranks: ApiChartRank[];
}

export const TOP_PRODUCTS_STRATEGIES: Record<
  TopProductsStrategyKey,
  TopProductsStrategy
> = {
  top_sellers: {
    label: "Top Sellers",
    description: "Products that generate the most revenue overall.",
    ranks: [
      {
        metric: ApiMetric.METRIC_PRODUCT_REVENUE,
        direction: ApiChartRankDrection.DESC,
        weight: 1,
      },
    ],
  },
  best_converting_products: {
    label: "Best Converting Products",
    description:
      "Products with the highest conversion rate and enough traffic to be statistically meaningful.",
    ranks: [
      {
        metric: ApiMetric.METRIC_VISIT_TO_PURCHASE_RATE,
        direction: ApiChartRankDrection.DESC,
        weight: 0.8,
      },
      {
        metric: ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT,
        direction: ApiChartRankDrection.DESC,
        weight: 0.2,
      },
    ],
  },
  highly_clicked_products: {
    label: "Highly Clicked Products",
    description:
      "Products that get the most click-through from listings or discovery.",
    ranks: [
      {
        metric: ApiMetric.METRIC_PRODUCT_CLICK_THROUGH_RATE,
        direction: ApiChartRankDrection.DESC,
        weight: 0.8,
      },
      {
        metric: ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT,
        direction: ApiChartRankDrection.DESC,
        weight: 0.2,
      },
    ],
  },
  cart_magnets: {
    label: "Cart Magnets",
    description: "Products that are added to cart most often.",
    ranks: [
      {
        metric: ApiMetric.METRIC_PAGE_ADD_TO_CART_CLICK_RATE,
        direction: ApiChartRankDrection.DESC,
        weight: 0.8,
      },
      {
        metric: ApiMetric.METRIC_PAGE_ADD_TO_CART_CLICK_COUNT,
        direction: ApiChartRankDrection.DESC,
        weight: 0.2,
      },
    ],
  },
  hidden_gems: {
    label: "Hidden Gems",
    description:
      "High-converting products that haven’t had much traffic yet.",
    ranks: [
      {
        metric: ApiMetric.METRIC_VISIT_TO_PURCHASE_RATE,
        direction: ApiChartRankDrection.DESC,
        weight: 0.8,
      },
      {
        metric: ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT,
        direction: ApiChartRankDrection.ASC,
        weight: 0.2,
      },
    ],
  },
  popular_with_returning_customers: {
    label: "Popular With Returning Customers",
    description: "Products that returning customers buy most often.",
    ranks: [
      {
        metric: ApiMetric.METRIC_RETURNING_CUSTOMER_RATE,
        direction: ApiChartRankDrection.DESC,
        weight: 0.8,
      },
      {
        metric: ApiMetric.METRIC_RETURNING_CUSTOMERS_COUNT,
        direction: ApiChartRankDrection.DESC,
        weight: 0.2,
      },
    ],
  },
  new_customer_favourites: {
    label: "New Customer Favourites",
    description: "Products that new customers buy most often.",
    ranks: [
      {
        metric: ApiMetric.METRIC_TO_NEW_CUSTOMER_RATE,
        direction: ApiChartRankDrection.DESC,
        weight: 0.8,
      },
      {
        metric: ApiMetric.METRIC_NEW_CUSTOMERS_COUNT,
        direction: ApiChartRankDrection.DESC,
        weight: 0.2,
      },
    ],
  },
};

export const LAST_N_DAYS_OPTIONS = [30, 60, 90, 120, 360] as const;
export const DEFAULT_LAST_N_DAYS = 120;

export function useTopProductsFromAnalytics() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(undefined);

  const fetchTopProductNames = useCallback(
    async (
      projectId: number,
      strategyKey: TopProductsStrategyKey | null,
      limit: number,
      lastNDays: number = DEFAULT_LAST_N_DAYS,
      customRanks?: ApiChartRank[],
    ): Promise<string[]> => {
      setLoading(true);
      setError(undefined);
      try {
        const strategy = strategyKey ? TOP_PRODUCTS_STRATEGIES[strategyKey] : null;
        const ranks =
          customRanks && customRanks.length > 0 ? customRanks : strategy?.ranks;
        if (!ranks || ranks.length === 0) return [];
        const chart: IChart = {
          name: "",
          dimension: [ApiDimension.DIMENSION_PRODUCT],
          template: ApiChartTemplate.SNAPSHOT,
          type: ApiChartType.TABLE,
          attributes: [],
          ranks,
          limit: Math.max(1, Math.min(limit, 100)),
          orderBy: `${ApiMetric.METRIC_RANK} DESC`,
          yAxisKeys: [
            {
              key: "m",
              fields: [
                ApiMetric.METRIC_RANK,
                ...Array.from(new Set(ranks.map((rank) => rank.metric as ApiMetric))),
              ],
            },
          ],
        };

        const res = await getPrimaryAnalytics(
          projectId,
          chart,
          undefined,
          undefined,
          lastNDays,
        );

        const productNames: string[] = [];
        const dimKey = ApiDimension.DIMENSION_PRODUCT;
        for (const row of res.chartData || []) {
          const raw = row[dimKey] ?? row.name;
          if (raw === undefined || raw === null) continue;
          const name = typeof raw === "string" ? raw : String(raw);
          if (name.trim()) {
            productNames.push(name);
          }
        }
        return productNames;
      } catch (err) {
        setError(err);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { fetchTopProductNames, loading, error };
}
